import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Article as ArticleIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../../../api/api';

const escapeHtml = value => String(value).replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
const validMargins = value => value && ['top','bottom','left','right'].every(key => Number.isFinite(Number(value[key])) && Number(value[key]) >= 0)
  && Number(value.left) + Number(value.right) < 210 && Number(value.top) + Number(value.bottom) < 297;
const normalizeMargins = value => validMargins(value) ? Object.fromEntries(Object.entries(value).map(([key,n])=>[key,Number(n)])) : {top:20,bottom:20,left:20,right:20};

const ContractEditDialog = ({ open, onClose, deal, template }) => {
  const loadVersion = useRef(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [contractContent, setContractContent] = useState('');
  const [fullClientData, setFullClientData] = useState(null);
  
  // Состояние для отступов страницы (такое же как в DocumentEditor)
  const [pageMargins, setPageMargins] = useState(() => {
    // Пытаемся загрузить отступы из localStorage
    const savedMargins = localStorage.getItem('documentEditor_pageMargins');
    if (savedMargins) {
      try {
        return normalizeMargins(JSON.parse(savedMargins));
      } catch (error) {
        console.error('Ошибка загрузки сохраненных отступов:', error);
      }
    }
    // Возвращаем дефолтные значения
    return {
      top: 20,
      bottom: 20,
      left: 20,
      right: 20
    };
  });

  // Функция загрузки полных данных клиента
  const loadFullClientData = async (clientId) => {
    try {
      const response = await api.get(`/clients/${clientId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setFullClientData(response.data);
      return response.data;
    } catch (error) {
      console.error('Ошибка загрузки данных клиента:', error);
      return null;
    }
  };

  // Подстановка данных в шаблон
  useEffect(() => {
    const version = ++loadVersion.current;
    if (open && template && deal) {
      loadAndProcessTemplate(() => loadVersion.current === version);
    }
    return () => { loadVersion.current++; };
  }, [open, template, deal]);

  const loadAndProcessTemplate = async (isCurrent) => {
    setLoading(true);
    setError('');
    try {
      // Сначала загружаем полные данные клиента, если есть client_id
      let clientDataToUse = null;
      if (deal?.client?.id) {
        clientDataToUse = await loadFullClientData(deal.client.id);
      } else if (deal?.client_id) {
        clientDataToUse = await loadFullClientData(deal.client_id);
      }

      if (!isCurrent()) return;
      // Пробуем использовать новый endpoint для рендеринга с данными сделки
      if (deal?.id) {
        try {
          const response = await api.post(`/documents/templates/${template.id}/render-with-deal/${deal.id}`, {}, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          
          if (!isCurrent()) return;
          if (!isCurrent()) return;
      // Загружаем отступы из ответа если они есть
          if (response.data.page_margins) {
            setPageMargins(normalizeMargins(response.data.page_margins));
            localStorage.setItem('documentEditor_pageMargins', JSON.stringify(response.data.page_margins));
          }
          
          // Сразу генерируем PDF предпросмотр
          const generated = await generatePDFPreview(response.data.content, normalizeMargins(response.data.page_margins || pageMargins), isCurrent);
          if (generated && isCurrent()) onClose();
          return;
        } catch (backendError) {
          if (!isCurrent()) return;
          console.warn('Ошибка backend endpoint, переходим на локальную обработку');
          // Продолжаем с локальной обработкой
        }
      }
      
      // Локальная обработка (резервный вариант)
      const response = await api.get(`/documents/templates/${template.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!isCurrent()) return;
      // Загружаем отступы из ответа если они есть
      if (response.data.page_margins) {
        setPageMargins(normalizeMargins(response.data.page_margins));
        localStorage.setItem('documentEditor_pageMargins', JSON.stringify(response.data.page_margins));
      }

      let templateContent = response.data.content || template.content || '<p>Пустой шаблон</p>';
      
      // Создаем обогащенный объект сделки с полными данными клиента
      const enrichedDeal = {
        ...deal,
        client: clientDataToUse || deal.client
      };
      
      const processedContent = substituteVariables(templateContent, enrichedDeal);
      
      // Сразу генерируем PDF предпросмотр
      const generated = await generatePDFPreview(processedContent, normalizeMargins(response.data.page_margins || pageMargins), isCurrent);
      if (generated && isCurrent()) onClose();
    } catch (error) {
      console.error('Ошибка загрузки шаблона:', error);
      if (isCurrent()) setError('Ошибка загрузки шаблона документа');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  // Функция генерации динамической таблицы товаров
  const generateItemsTable = (dealData) => {
    let items = [];

    // Функция форматирования даты и времени без часового пояса
    const formatDateTime = (dateString) => {
      if (!dateString) return 'Не указано';
      const date = new Date(dateString);
      return date.toLocaleString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // Получаем ИСХОДНУЮ сумму БЕЗ скидки/наценки
    const originalAmount = parseFloat(dealData?.amount) ||
                          parseFloat(dealData?.original_amount) ||
                          parseFloat(dealData?.rental_price) ||
                          0;

    // Получаем итоговую сумму С учетом скидки/наценки
    const suppliedTotal = dealData?.total_amount_with_discount ?? dealData?.total_amount;
    const totalAmountWithDiscount = suppliedTotal != null && Number.isFinite(Number(suppliedTotal)) ? Number(suppliedTotal) : originalAmount;

    // Форматируем период аренды
    const formattedPeriod = `${formatDateTime(dealData.start_date)} - ${formatDateTime(dealData.end_date)}`;

    // Пытаемся получить товары из разных источников
    if (dealData?.rental_assets && Array.isArray(dealData.rental_assets) && dealData.rental_assets.length > 0) {
      // Проверяем наличие индивидуальных цен (могут быть в разных полях)
      const hasRentalPrices = dealData.rental_assets.some(ra => 
        parseFloat(ra.rental_price) > 0 || 
        parseFloat(ra.total_amount) > 0 || 
        parseFloat(ra.rental_price_per_period) > 0
      );

      if (hasRentalPrices) {
        // Используем индивидуальные цены активов БЕЗ скидки
        items = dealData.rental_assets.map((rentalAsset, index) => ({
          name: rentalAsset.asset?.name || 'Неизвестный товар',
          quantity: rentalAsset.quantity || 1,
          // Берём цену из rental_price, total_amount или rental_price_per_period
          price: parseFloat(rentalAsset.rental_price) || parseFloat(rentalAsset.total_amount) || parseFloat(rentalAsset.rental_price_per_period) || 0,
          period: formattedPeriod
        }));
      } else {
        // Распределяем ИСХОДНУЮ сумму между активами пропорционально
        const totalItems = dealData.rental_assets.reduce((sum, ra) => sum + (ra.quantity || 1), 0);
        items = dealData.rental_assets.map((rentalAsset, index) => {
          const quantity = rentalAsset.quantity || 1;
          const pricePerItem = (originalAmount / totalItems) * quantity;
          return {
            name: rentalAsset.asset?.name || 'Неизвестный товар',
            quantity: quantity,
            price: pricePerItem,
            period: formattedPeriod
          };
        });
      }
    } else if (dealData.assets && Array.isArray(dealData.assets) && dealData.assets.length > 0) {
      // Если много активов через поле assets
      const pricePerAsset = originalAmount / dealData.assets.length;
      items = dealData.assets.map((asset, index) => ({
        name: asset.name || 'Неизвестный товар',
        quantity: 1,
        price: pricePerAsset,
        period: formattedPeriod
      }));
    } else if (dealData.asset?.name) {
      // Один актив
      items = [{
        name: dealData.asset.name,
        quantity: 1,
        price: originalAmount,
        period: formattedPeriod
      }];
    }

    // Если товаров нет, добавляем заглушку
    if (items.length === 0) {
      items = [{
        name: 'Товар не указан',
        quantity: 1,
        price: originalAmount,
        period: 'Период не указан'
      }];
    }

    // Получаем информацию о скидке/наценке
    const discountAmount = parseFloat(dealData?.discount_amount) || 0;
    const discountType = dealData?.discount_type || 'fixed';

    // Определяем, это скидка или наценка по знаку числа
    // Отрицательное число (например -500) = скидка
    // Положительное число (например 500) = наценка
    const isDiscount = discountAmount < 0;
    const discountText = isDiscount ? 'Скидка' : 'Наценка';
    const discountValue = Math.abs(discountAmount);

    // Генерируем HTML таблицы БЕЗ инлайн стилей - только через CSS
    let tableHTML = '<table>';

    // Заголовок таблицы
    tableHTML += '<tr>';
    tableHTML += '<th>Наименование инвентаря</th>';
    tableHTML += '<th>Количество</th>';
    tableHTML += '<th>Срок аренды</th>';
    tableHTML += '<th>Стоимость аренды</th>';
    tableHTML += '</tr>';

    // Строки с товарами (показываем цены БЕЗ скидки/наценки)
    items.forEach(item => {
      tableHTML += '<tr>';
      tableHTML += `<td>${escapeHtml(item.name)}</td>`;
      tableHTML += `<td>${item.quantity} шт.</td>`;
      tableHTML += `<td>${item.period}</td>`;
      tableHTML += `<td>${(item.price || 0).toFixed(2)} ₽</td>`;
      tableHTML += '</tr>';
    });

    // Если есть скидка/наценка, добавляем строку
    if (discountAmount !== 0) {
      tableHTML += '<tr style="border-top: 1px solid #000;">';
      tableHTML += `<td colspan="3" style="text-align: right;">${discountText}:</td>`;

      if (discountType === 'percentage') {
        // Процентная скидка/наценка
        const percentValue = discountValue;
        const amountValue = originalAmount * (percentValue / 100);
        tableHTML += `<td style="text-align: center;">${discountValue}% (${isDiscount ? '-' : '+'}${amountValue.toFixed(2)} ₽)</td>`;
      } else {
        // Фиксированная скидка/наценка
        tableHTML += `<td style="text-align: center;">${isDiscount ? '-' : '+'}${discountValue.toFixed(2)} ₽</td>`;
      }
      tableHTML += '</tr>';
    }

    // Добавляем строку с итоговой суммой (С учетом скидки/наценки)
    tableHTML += '<tr style="border-top: 2px solid #000; font-weight: bold; background-color: #f5f5f5;">';
    tableHTML += '<td style="text-align: right; font-weight: bold;">ИТОГО К ОПЛАТЕ:</td>';
    tableHTML += `<td style="text-align: center; font-weight: bold;">${items.length} поз.</td>`;
    tableHTML += '<td style="text-align: center; font-weight: bold;">-</td>';
    tableHTML += `<td style="text-align: center; font-weight: bold;">${totalAmountWithDiscount.toFixed(2)} ₽</td>`;
    tableHTML += '</tr>';

    tableHTML += '</table>';

    return tableHTML;
  };

  // Функция подстановки переменных
  const substituteVariables = (content, dealData) => {
    let processedContent = content;

    // Сначала обрабатываем динамическую таблицу товаров
    const itemsTableRegex = /\{ITEMS_TABLE\}/g;
    const hasItemsTable = itemsTableRegex.test(processedContent);
    
    if (hasItemsTable) {
      const itemsTable = generateItemsTable(dealData);
      processedContent = processedContent.replace(/\{ITEMS_TABLE\}/g, itemsTable);
    }

    // Данные клиента
    const clientName = dealData.client?.display_name || 
                      dealData.client?.name || 
                      `${dealData.client?.first_name || ''} ${dealData.client?.last_name || ''}`.trim() ||
                      'Не указано';
    
    const clientPhone = dealData.client?.phone || 
                       dealData.client?.contact_info || 
                       'Не указан';
    
    const clientEmail = dealData.client?.email || 'Не указан';

    // Данные активов
    const assetNames = getAssetNames(dealData);
    const totalAmount = parseFloat(dealData.amount) || 0;
    const deposit = calculateDeposit(dealData);

    // Данные сделки
    const startDate = dealData.start_date ? new Date(dealData.start_date).toLocaleDateString('ru-RU') : 'Не указана';
    const endDate = dealData.end_date ? new Date(dealData.end_date).toLocaleDateString('ru-RU') : 'Не указана';
    const contractNumber = dealData.deal_number || 'Не указан';
    const contractDate = new Date().toLocaleDateString('ru-RU');
    
    // Пункт аренды (если есть)
    const rentalPoint = dealData.rental_point?.name || 'Основной офис';

    // Словарь замен
    const variables = {
      'client_name': clientName,
      'client_phone': clientPhone,
      'client_email': clientEmail,
      'passport_series': dealData.client?.passport_series || 'Не указана',
      'passport_number': dealData.client?.passport_number || 'Не указан',
      'passport_issued_by': dealData.client?.passport_issued_by || 'Не указано',
      'passport_issued_date': dealData.client?.passport_issued_date ? 
        new Date(dealData.client.passport_issued_date).toLocaleDateString('ru-RU') : 'Не указана',
      'client_address': dealData.client?.address || 'Не указан',
      'asset_name': assetNames,
      'rental_price': `${totalAmount} ₽`,
      'total_amount': `${totalAmount} ₽`,
      'start_date': startDate,
      'end_date': endDate,
      'rental_point': rentalPoint,
      'deposit': `${deposit} ₽`,
      'contract_number': contractNumber,
      'contract_date': contractDate
    };

    // Заменяем переменные в контенте
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      processedContent = processedContent.replace(regex, () => escapeHtml(value));
    });

    return processedContent;
  };

  // Получение названий активов
  const getAssetNames = (dealData) => {
    if (dealData.asset_names && dealData.asset_names !== "Нет активов") {
      return dealData.asset_names;
    }

    if (dealData.rental_assets && Array.isArray(dealData.rental_assets) && dealData.rental_assets.length > 0) {
      return dealData.rental_assets
        .map(rentalAsset => rentalAsset.asset?.name || 'Неизвестный актив')
        .filter(name => name !== 'Неизвестный актив')
        .join(', ');
    }
    
    if (dealData.assets && Array.isArray(dealData.assets) && dealData.assets.length > 0) {
      return dealData.assets.map(asset => asset.name).join(", ");
    }
    
    if (dealData.asset?.name) {
      return dealData.asset.name;
    }
    
    return 'Не указано';
  };

  // Расчет залога
  const calculateDeposit = (dealData) => {
    return dealData.deposit || 0;
  };

  const handleContentChange = (newContent) => {
    setContractContent(newContent);
  };

  // Функция генерации PDF предпросмотра
  const generatePDFPreview = async (content, margins, isCurrent) => {
    let printContainer, styleElement;
    try {
      // Динамический импорт библиотек
      const { default: jsPDF } = await import('jspdf');
      const { default: html2canvas } = await import('html2canvas');
      if (!isCurrent()) return false;

      // Создаем временный контейнер для печати БЕЗ padding - отступы через стили
      printContainer = document.createElement('div');
      printContainer.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        width: calc(210mm - ${margins.left}mm - ${margins.right}mm);
        background: white;
        padding: 0;
        font-family: 'Times New Roman', serif;
        line-height: 1.6;
        color: #000;
        box-sizing: border-box;
      `;
      
      printContainer.innerHTML = content;
      
      // Очищаем resize элементы и проблемные width стили таблиц
      const tables = printContainer.querySelectorAll('table');
      const containerWidthPx = printContainer.offsetWidth || 500;
      
      tables.forEach(table => {
        // Удаляем все resize элементы (полоски для изменения размера)
        table.querySelectorAll('.column-resizer, .row-resizer, .table-column-resizer').forEach(el => {
          el.remove();
        });
        
        // Проверяем, есть ли фиксированная ширина в пикселях
        const currentWidth = table.style.width;
        const widthAttr = table.getAttribute('width');
        
        // Если ширина задана в пикселях и больше контейнера - очищаем
        if (currentWidth && currentWidth.includes('px')) {
          const widthValue = parseInt(currentWidth);
          if (widthValue > containerWidthPx) {
            table.style.width = '';
          }
        }
        
        // Если ширина задана атрибутом и больше контейнера - очищаем
        if (widthAttr && !widthAttr.includes('%')) {
          const widthValue = parseInt(widthAttr);
          if (widthValue > containerWidthPx) {
            table.removeAttribute('width');
          }
        }
        
        // Очищаем проблемные стили ячеек
        const cells = table.querySelectorAll('td, th');
        cells.forEach(cell => {
          // Удаляем resize стили у ячеек
          cell.style.resize = 'none';
          
          if (cell.style.width && cell.style.width.includes('px')) {
            const cellWidth = parseInt(cell.style.width);
            // Очищаем только очень широкие ячейки
            if (cellWidth > containerWidthPx / 2) {
              cell.style.width = '';
            }
          }
          
          // Удаляем атрибуты width у ячеек
          if (cell.getAttribute('width')) {
            cell.removeAttribute('width');
          }
        });
      });
      
      // Добавляем стили для таблиц (точно как в DocumentEditor)
      styleElement = document.createElement('style');
      styleElement.textContent = `
        table {
          border-collapse: collapse !important;
          width: 100% !important;
          margin: 15px 0 !important;
          border: 2px solid #000 !important;
        }
        td, th {
          border: 1px solid #000 !important;
          padding: 12px !important;
          text-align: left !important;
          vertical-align: top !important;
        }
        th {
          background-color: #f0f0f0 !important;
          font-weight: bold !important;
        }
      `;
      
      document.body.appendChild(printContainer);
      document.head.appendChild(styleElement);

      // Создаем canvas
      const canvas = await html2canvas(printContainer, {
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      if (!isCurrent()) return false;
      // Удаляем временные элементы
      document.body.removeChild(printContainer);
      document.head.removeChild(styleElement);

      // Создаем PDF с правильными отступами
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Размеры PDF страницы A4 в мм
      const pdfWidth = 210;
      const pdfHeight = 297;
      const contentWidth = pdfWidth - margins.left - margins.right;
      const maxContentHeight = pdfHeight - margins.top - margins.bottom;
      
      // Соотношение пикселей к мм
      const pxPerMm = canvas.width / contentWidth;
      
      // Высота контента одной страницы в пикселях
      const pageHeightPx = maxContentHeight * pxPerMm;
      
      // Количество страниц
      const totalPages = Math.ceil(canvas.height / pageHeightPx);
      
      // Создаем временный canvas для нарезки страниц
      const pageCanvas = document.createElement('canvas');
      const pageCtx = pageCanvas.getContext('2d');
      pageCanvas.width = canvas.width;
      
      for (let page = 0; page < totalPages; page++) {
        // Вычисляем высоту текущей страницы
        const startY = page * pageHeightPx;
        const currentPageHeightPx = Math.min(pageHeightPx, canvas.height - startY);
        
        // Устанавливаем высоту canvas для текущей страницы
        pageCanvas.height = currentPageHeightPx;
        
        // Очищаем и рисуем часть исходного canvas
        pageCtx.fillStyle = '#ffffff';
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        pageCtx.drawImage(
          canvas,
          0, startY,
          canvas.width, currentPageHeightPx,
          0, 0,
          canvas.width, currentPageHeightPx
        );
        
        // Конвертируем в изображение
        const pageImgData = pageCanvas.toDataURL('image/png');
        
        // Добавляем новую страницу (кроме первой)
        if (page > 0) {
          pdf.addPage();
        }
        
        // Размер изображения на странице PDF
        const imgWidth = contentWidth;
        const imgHeight = (currentPageHeightPx / pxPerMm);
        
        // Добавляем изображение с отступами
        pdf.addImage(pageImgData, 'PNG', margins.left, margins.top, imgWidth, imgHeight);
      }

      // Открываем PDF в новом окне для предпросмотра и печати
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      const printWindow = window.open(pdfUrl, '_blank');
      if (!printWindow) { URL.revokeObjectURL(pdfUrl); throw new Error('Окно предпросмотра заблокировано'); }
      if (printWindow) {
        printWindow.onload = function() {
          setTimeout(() => {
            printWindow.print();
          }, 1000);
        };
      }
      
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 10000);
      return true;
    } catch (error) {
      console.error('Ошибка создания PDF:', error);
      if (isCurrent()) setError('Ошибка создания PDF предпросмотра');
      return false;
    } finally {
      printContainer?.remove();
      styleElement?.remove();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ArticleIcon color="primary" />
            <Typography variant="h6">
              {template?.name || 'Договор'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {deal && (
              <>
                <Chip 
                  label={`№ ${deal.deal_number}`}
                  color="primary"
                  size="small"
                />
                <Chip 
                  label={deal.client?.display_name || deal.client?.name || 'Клиент не указан'}
                  color="default"
                  size="small"
                />
              </>
            )}
            
            <Button 
              onClick={onClose}
              startIcon={<CloseIcon />}
              variant="outlined"
              size="small"
            >
              Закрыть
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            gap: 2
          }}>
            <CircularProgress size={60} />
            <Typography variant="h6" color="primary">
              Создание PDF предпросмотра...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Договор будет открыт в новом окне для печати
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default ContractEditDialog;
